var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo, } from "react";
import { isMobile as isDetectedMobile } from "react-device-detect";
import { Icon } from "../../../icon";
import { KeyBoardKeys } from "../../../types";
import { Flyout } from "../../../navigation";
import { SelectCustom } from "../customSelect/SelectCustom.styles";
import { Placeholder, SelectNative } from "../nativeSelect/SelectNative.styles";
import { Trigger } from "../trigger/Trigger.styles";
import { OptionsContainer } from "../customSelect/OptionsContainer.styles";
import { CustomOption, IconWrapper } from "../customSelect/CustomOption.styles";
import { ErrorLabel } from "../../common/Error.styles";
import { Label } from "../label/Label.styles";
import { SelectWrapper } from "./DropdownWrapper.styles";
import { DropdownContainer, FooterContainer } from "./Dropdown.styles";
// utils
import { getInitialSelectedValueIndex, getOptionByValue } from "../utils";
import { makeDataTestIdGetter } from "../../../utils";
import { useMedia } from "../../../utils/use-media";
export const Dropdown = (_a) => {
    var _b;
    var { onChange, onBlur, errorMessage, errors, name, values, label, placeholder, readOnly, required, disabled, items, defaultValue, className, flyoutMaxItemsCount = 5, multiple, previewId, children, forceCustom, isOpen, renderFooter, resetDefaultValue, dataTestIdPrefix } = _a, props = __rest(_a, ["onChange", "onBlur", "errorMessage", "errors", "name", "values", "label", "placeholder", "readOnly", "required", "disabled", "items", "defaultValue", "className", "flyoutMaxItemsCount", "multiple", "previewId", "children", "forceCustom", "isOpen", "renderFooter", "resetDefaultValue", "dataTestIdPrefix"]);
    const nativeSelect = useRef(null);
    const customSelect = useRef(null);
    const isMobile = useMedia(["(min-width: 1024px)", "(min-width: 768px)", "(min-width: 320px)"], [false, false, true], false);
    const [isFocused, setIsFocused] = useState(false);
    const [isOpened, setIsOpened] = useState(false);
    const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1); // for single select
    const [selectedOptionsIndexes, setSelectedOptionsIndexes] = useState([]); // for multi select
    const [isOpenedMulti, setIsOpenedMulti] = useState(false);
    const [managedSelectedOptionIndex, setManagedSelectedOptionIndex] = useState(getInitialSelectedValueIndex({ defaultValue, values, name, items }, multiple));
    const selectHasBeenTouched = useRef(false);
    useEffect(() => {
        if (resetDefaultValue && !defaultValue) {
            setManagedSelectedOptionIndex(-1);
        }
    }, [resetDefaultValue, defaultValue]);
    // If the value has not been set yet, updates to the defaultValue prop should overwrite the value
    useEffect(() => {
        if (defaultValue && items.length && !selectHasBeenTouched.current) {
            setManagedSelectedOptionIndex(getInitialSelectedValueIndex({ defaultValue, values, name, items }, multiple));
        }
    }, [defaultValue]);
    const unManagedSelectedOptionIndex = useMemo(() => getInitialSelectedValueIndex({ defaultValue, values, name, items }, multiple), [defaultValue, values, name, items]);
    useEffect(() => {
        if (isOpen !== isOpened) {
            setIsOpened(isOpen);
        }
    }, [isOpen]);
    useEffect(() => {
        if (!multiple) {
            if (unManagedSelectedOptionIndex > -1) {
                setSelectedOptionIndex(unManagedSelectedOptionIndex);
            }
            else {
                setSelectedOptionIndex(managedSelectedOptionIndex);
            }
        }
        else if (unManagedSelectedOptionIndex > -1) {
            setSelectedOptionsIndexes(unManagedSelectedOptionIndex);
        }
        else {
            setSelectedOptionsIndexes(managedSelectedOptionIndex);
        }
    }, [managedSelectedOptionIndex, unManagedSelectedOptionIndex]);
    const [customOptionHoveredIndex, setCustomSelectHoveredIndex] = useState(managedSelectedOptionIndex >= 0 ? managedSelectedOptionIndex : 0);
    function updateNativeSelect(optionIndex) {
        if (multiple) {
            if (!optionIndex.length)
                return;
            const option = items[optionIndex];
            if (nativeSelect && nativeSelect.current && option) {
                const { value } = option;
                nativeSelect.current.value = value;
            }
        }
        else {
            if (optionIndex < 0)
                return;
            const option = items[optionIndex];
            if (nativeSelect && nativeSelect.current && option) {
                const { value } = option;
                nativeSelect.current.value = value;
            }
        }
    }
    useEffect(() => {
        if (!multiple) {
            const index = getInitialSelectedValueIndex({
                defaultValue,
                values,
                name,
                items,
            });
            setManagedSelectedOptionIndex(index);
            // updateNativeSelect(index); // placeholder ist the first one.
            // todo find out why index is number | number[]
            if (index > -1) {
                setCustomSelectHoveredIndex(index);
            }
            else
                setCustomSelectHoveredIndex(0);
        }
        else if (multiple && onChange !== undefined) {
            setManagedSelectedOptionIndex(getInitialSelectedValueIndex({ defaultValue, values, name, items }, multiple));
        }
    }, [values]);
    function watchClickOutside(e) {
        const didClickedOutside = !customSelect.current.contains(e.target);
        if (didClickedOutside) {
            setIsOpened(false);
            setCustomSelectHoveredIndex(0);
        }
    }
    const getPreviousOption = () => {
        // skip disabled items
        for (let maybeNextIndex = customOptionHoveredIndex + 1; maybeNextIndex < items.length; maybeNextIndex += 1) {
            if (!items[maybeNextIndex].disabled) {
                return maybeNextIndex;
            }
        }
        return customOptionHoveredIndex;
    };
    const getNextOption = () => {
        // skip disabled items
        for (let maybeNextIndex = customOptionHoveredIndex - 1; maybeNextIndex >= 0; maybeNextIndex -= 1) {
            if (!items[maybeNextIndex].disabled) {
                return maybeNextIndex;
            }
        }
        // fallback
        return customOptionHoveredIndex;
    };
    const handleKeyboardNavigation = (event) => {
        event.preventDefault(); // prevent page scrolling
        // press down -> go next
        if (event.key === KeyBoardKeys.ArrowDown &&
            customOptionHoveredIndex < items.length - 1) {
            setCustomSelectHoveredIndex(getPreviousOption());
            if (customOptionHoveredIndex > flyoutMaxItemsCount - 2) {
                const options = customSelect.current.querySelectorAll("li");
                if (options && !multiple) {
                    const nextOption = options[customOptionHoveredIndex + 1];
                    let nextOptionHeight = 40;
                    if (nextOption) {
                        nextOptionHeight = nextOption.getBoundingClientRect().height;
                    }
                    customSelect.current.lastChild.scrollBy(0, nextOptionHeight);
                }
            }
        }
        if (event.key === KeyBoardKeys.ArrowUp && customOptionHoveredIndex > 0) {
            setCustomSelectHoveredIndex(getNextOption());
            if (customOptionHoveredIndex > flyoutMaxItemsCount - 2) {
                const options = customSelect.current.querySelectorAll("li");
                if (options && !multiple) {
                    const nextOption = options[customOptionHoveredIndex];
                    if (nextOption) {
                        const nextOptionHeight = nextOption.getBoundingClientRect().height;
                        customSelect.current.lastChild.scrollBy(0, -1 * nextOptionHeight);
                    }
                }
            }
        }
        // press Enter or space -> select the option
        if (event.key === KeyBoardKeys.Enter || event.key === KeyBoardKeys.Space) {
            updateNativeSelect(customOptionHoveredIndex);
            setManagedSelectedOptionIndex(customOptionHoveredIndex);
            selectHasBeenTouched.current = true;
            setIsOpened(false);
        }
        // press ESC -> close selectCustom
        if (event.key === KeyBoardKeys.Escape) {
            setIsOpened(false);
        }
    };
    const callbackRef = useRef(handleKeyboardNavigation);
    useEffect(() => {
        callbackRef.current = handleKeyboardNavigation;
    }, [customOptionHoveredIndex]); // update after each render
    const generateSelectedTextForMultiselect = () => {
        const text = selectedOptionsIndexes.map((e) => { var _a; return ((_a = items === null || items === void 0 ? void 0 : items[e]) === null || _a === void 0 ? void 0 : _a.text) ? `${items[e].text}` : placeholder; });
        return text.join(", ");
    };
    useEffect(() => {
        const callback = (e) => callbackRef.current(e);
        const unmountHandler = () => {
            if (!multiple) {
                document.removeEventListener("keydown", callback);
            }
            document.removeEventListener("click", watchClickOutside);
        };
        if (!isOpened) {
            return unmountHandler;
        }
        if (!multiple) {
            document.addEventListener("keydown", callback);
        }
        document.addEventListener("click", watchClickOutside);
        return unmountHandler;
    }, [isOpened, isFocused, customOptionHoveredIndex]);
    const size = multiple && isFocused
        ? items.length + 1
        : multiple && isMobile && !forceCustom
            ? items.length + (disabled ? 0 : 1)
            : undefined;
    const showIcon = !readOnly && !(multiple && isFocused);
    let selectedOption = typeof managedSelectedOptionIndex === "number"
        ? items[managedSelectedOptionIndex]
        : null;
    const getDataTestId = makeDataTestIdGetter(dataTestIdPrefix);
    return (_jsxs(DropdownContainer, Object.assign({ className: "select" }, { children: [label && (_jsxs(Label, Object.assign({ id: "label" }, { children: [label, label && required && "*"] }))), _jsxs(SelectWrapper, Object.assign({ readOnly: readOnly, opened: isOpened, multiple: multiple, className: readOnly || disabled ? " disabled" : "", "$forceCustom": forceCustom }, { children: [showIcon && _jsx(Icon, { withHitSpace: true, symbol: "chevron_down" }), _jsxs(SelectNative, Object.assign({}, props, { "data-preview-id": previewId, opened: isOpened, "$forceCustom": forceCustom, ref: nativeSelect, id: name, hasInitialSelectionChanged: Boolean(values === null || values === void 0 ? void 0 : values[name]) || selectedOptionIndex >= 0, "aria-labelledby": "label", focused: isFocused, size: size, onChange: (e) => {
                            selectHasBeenTouched.current = true;
                            if (multiple) {
                                const options = nativeSelect.current.querySelectorAll("option");
                                const selectedValues = Array.from(options).map((option, index) => !option.disabled && option.selected && index > 0
                                    ? index - 1
                                    : null);
                                const indexed = selectedValues.filter((v) => v !== null && v >= 0);
                                setSelectedOptionsIndexes(indexed);
                                return;
                            }
                            const option = getOptionByValue({
                                selectedValue: e.target.value,
                                items,
                            });
                            if (!option) {
                                setManagedSelectedOptionIndex(-1);
                                setCustomSelectHoveredIndex(0);
                            }
                            else {
                                setManagedSelectedOptionIndex(option.index);
                                setCustomSelectHoveredIndex(option.index);
                                if (unManagedSelectedOptionIndex > -1) {
                                    updateNativeSelect(unManagedSelectedOptionIndex);
                                }
                            }
                            if (onChange) {
                                onChange(option);
                            }
                        }, onKeyUp: (e) => {
                            setIsFocused(true);
                            if (e.key === " ") {
                                setIsOpenedMulti(true);
                            }
                            if (e.key === KeyBoardKeys.Tab || e.key === KeyBoardKeys.Enter) {
                                setIsOpenedMulti(false);
                            }
                        }, onBlur: (e) => {
                            setIsFocused(false);
                            if (onBlur)
                                onBlur(e);
                        }, multiOpened: isOpenedMulti && multiple, multiple: multiple, name: name, 
                        // for readonly fields use defaultvalue
                        disabled: readOnly || disabled, readOnly: readOnly, errors: errors, defaultValue: (multiple ? "" : values === null || values === void 0 ? void 0 : values[name]) || defaultValue, "data-testid": getDataTestId("dropdown-select") }, { children: [placeholder && (_jsx(Placeholder, Object.assign({ value: multiple ? "" : defaultValue || "" }, { children: placeholder }))), items === null || items === void 0 ? void 0 : items.map((option) => {
                                return option ? (_jsx("option", Object.assign({ disabled: !!option.disabled, value: option.value }, { children: option.text }), option.value)) : ("");
                            })] })), _jsxs(SelectCustom, Object.assign({ "data-preview-id": previewId, ref: customSelect, "aria-hidden": !isOpened, "$forceCustom": forceCustom }, { children: [_jsx(Trigger, Object.assign({}, props, { onClick: () => {
                                    setIsOpened((o) => {
                                        if (isDetectedMobile && isMobile && !forceCustom) {
                                            return o;
                                        }
                                        if (readOnly || disabled) {
                                            return o;
                                        }
                                        return !o;
                                    });
                                    if (isDetectedMobile && isMobile && !forceCustom) {
                                        setIsOpenedMulti(true);
                                    }
                                }, opened: isOpened, errors: errors, name: name, hasInitialSelectionChanged: Boolean(values === null || values === void 0 ? void 0 : values[name]) ||
                                    selectedOptionIndex >= 0 ||
                                    Boolean(selectedOptionsIndexes.length), className: readOnly || disabled ? " disabled" : "", disabled: disabled, readOnly: readOnly, "data-testid": getDataTestId("dropdown-selectcustom-trigger") }, { children: _jsx("div", { children: multiple
                                        ? items
                                            ? selectedOptionsIndexes.length
                                                ? generateSelectedTextForMultiselect()
                                                : placeholder || ((_b = items[0]) === null || _b === void 0 ? void 0 : _b.text)
                                            : ""
                                        : items
                                            ? (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.text) || placeholder
                                            : "" }) })), _jsxs(OptionsContainer, Object.assign({ flyoutMaxItemsCount: flyoutMaxItemsCount, opened: isOpened, isMultipleSelect: multiple, withFooter: !!renderFooter }, { children: [multiple && (_jsx(Flyout, { type: "checkbox", onItemsChange: (selectedItems) => {
                                            const selectedOptions = selectedItems.map((value) => getOptionByValue({ selectedValue: value, items }));
                                            setSelectedOptionsIndexes(selectedOptions.map((o) => o.index));
                                            if (onChange) {
                                                onChange(selectedOptions);
                                            }
                                        }, name: "checkbox-dropdown", values: selectedOptionsIndexes.map((e) => { var _a; return (_a = items === null || items === void 0 ? void 0 : items[e]) === null || _a === void 0 ? void 0 : _a.value; }), items: items })), !multiple &&
                                        (items === null || items === void 0 ? void 0 : items.map((option, index) => (_jsxs(CustomOption, Object.assign({ onMouseMove: () => {
                                                if (index !== customOptionHoveredIndex) {
                                                    setCustomSelectHoveredIndex(index);
                                                }
                                            }, isHovered: isOpened && index === customOptionHoveredIndex, isActive: managedSelectedOptionIndex === index, disabled: !!option.disabled, onClick: () => {
                                                selectHasBeenTouched.current = true;
                                                if (option.disabled) {
                                                    return;
                                                }
                                                setManagedSelectedOptionIndex(index);
                                                nativeSelect.current.selectedIndex = index;
                                                setIsOpened(false);
                                                if (onChange)
                                                    onChange(option);
                                            }, "data-testid": getDataTestId("dropdown-selectcustom-option"), "data-": "select-option" }, { children: [!option.disabled
                                                    ? index === managedSelectedOptionIndex && (_jsx(IconWrapper, { children: _jsx(Icon, { symbol: "Checkmark" }) }))
                                                    : null, _jsx("span", { children: option.text })] }), option.value)))), multiple && typeof renderFooter === "function" && (_jsx(FooterContainer, { children: _jsx(_Fragment, { children: renderFooter({
                                                closeDropdown: () => {
                                                    setIsOpened(false);
                                                },
                                            }) }) }))] })), isOpened && _jsx(_Fragment, { children: children })] }))] })), (errors === null || errors === void 0 ? void 0 : errors[name]) && (_jsx(ErrorLabel, { children: errorMessage || errors[name] }))] })));
};
